import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Row, Col } from 'antd';

import { CheckboxGroup, CheckboxGroupOpts } from 'common/components/CheckboxGroup';
import qa from 'qa-selectors';
import TableFilter from 'common/components/TableFilter';
import { confDefault } from 'conf';

interface Props extends WithTranslation {
    demoMode?: boolean;
    driversChecked: string[];
    driversOpts: CheckboxGroupOpts;
    transportStatesChecked: string[];
    transportStatesOpts: CheckboxGroupOpts;
    vehiclesChecked: string[];
    vehiclesOpts: CheckboxGroupOpts;
    onConfirm?: (driversChecked: string[], vehiclesChecked: string[], transitStatesChecked: string[]) => void;
    onCancel?: () => void;
}

interface State {
    driversChecked: string[];
    vehiclesChecked: string[];
    transportStatesChecked: string[];
}

class RouteOverviewFilter extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            driversChecked: props.driversChecked,
            vehiclesChecked: props.vehiclesChecked,
            transportStatesChecked: props.transportStatesChecked
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.driversChecked.length !== this.props.driversChecked.length) {
            this.setState({ driversChecked: this.props.driversChecked });
        }
        if (prevProps.vehiclesChecked.length !== this.props.vehiclesChecked.length) {
            this.setState({ vehiclesChecked: this.props.vehiclesChecked });
        }
    }

    render() {
        const { t } = this.props;
        return (
            <TableFilter
                className="route-overview-filter"
                qa={qa.routeOverview.filter.section}
                heading={t('RouteOverviewFilter.name')}
                body={
                    <Row gutter={[24, 24]}>
                        {this.props.driversOpts.length > 0 && (
                            <Col>
                                <CheckboxGroup
                                    heading={t('common.drivers')}
                                    scrolled={true}
                                    labelCheckAll={t('common.allDrivers')}
                                    qa={qa.routeOverview.filter.sectionDrivers}
                                    options={this.props.driversOpts.map(v => ({
                                        code: v.code,
                                        label: v.label,
                                        checked: this.state.driversChecked.includes(v.code)
                                    }))}
                                    onChange={this._onCheckboxDriversChange}
                                />
                            </Col>
                        )}
                        {this.props.vehiclesOpts.length > 0 && (
                            <Col>
                                <CheckboxGroup
                                    heading={t('common.vehicles')}
                                    scrolled={true}
                                    labelCheckAll={t('common.allVehicles')}
                                    qa={qa.routeOverview.filter.sectionVehicles}
                                    options={this.props.vehiclesOpts.map(v => ({
                                        code: v.code,
                                        label: v.label,
                                        checked: this.state.vehiclesChecked.includes(v.code)
                                    }))}
                                    onChange={this._onCheckboxVehiclesChange}
                                />
                            </Col>
                        )}
                        {this.props.transportStatesOpts.length > 0 && (
                            <Col>
                                <CheckboxGroup
                                    heading={t('RouteOverviewFilter.transport')}
                                    scrolled={true}
                                    labelCheckAll={t('RouteOverviewFilter.states')}
                                    qa={qa.routeOverview.filter.sectionStates}
                                    options={this.props.transportStatesOpts.map(states => ({
                                        code: states.code,
                                        label: t(`RouteOverviewFilter.${states.label}`),
                                        checked: this.state.transportStatesChecked.includes(states.code)
                                    }))}
                                    onChange={this._onCheckboxTransportStateChange}
                                />
                            </Col>
                        )}
                    </Row>
                }
                cancelButton={{
                    onClick: this._onCancelClick,
                    qa: qa.routeOverview.filter.btnCancel
                }}
                submitButton={{
                    onClick: this._onConfirmClick,
                    qa: qa.routeOverview.filter.btnConfirm,
                    disabled: this.props.demoMode
                }}
                resetButton={{
                    onClick: this._onResetClick
                }}
            />
        );
    }

    private _onResetClick = () => {
        const defaults = confDefault.settings.scheduling.routeOverview.filter;
        this.setState({
            driversChecked: defaults.drivers,
            vehiclesChecked: defaults.vehicles,
            transportStatesChecked: this.props.transportStatesOpts.map(state => state.code)
        });
    };

    private _onConfirmClick = () => {
        this.props.onConfirm?.(
            this.state.driversChecked,
            this.state.vehiclesChecked,
            this.state.transportStatesChecked
        );
    };

    private _onCancelClick = () => {
        this.props.onCancel?.();
    };

    private _onCheckboxDriversChange = (driversChecked: string[]) => {
        this.setState({
            driversChecked
        });
    };

    private _onCheckboxVehiclesChange = (vehiclesChecked: string[]) => {
        this.setState({
            vehiclesChecked
        });
    };

    private _onCheckboxTransportStateChange = (transportStatesChecked: string[]) => {
        this.setState({
            transportStatesChecked
        });
    };
}

export default withTranslation()(RouteOverviewFilter);
